// AddAppointment.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Classes from "./AddAppointment.module.css";

import Step1Appointment from "./Comp/Step1Appointment";
import Step2Vehicle from "./Comp/Step2Vehicle";
import Step3Location from "./Comp/Step3Location";
import ConfirmBookingModal from "./Comp/ConfirmBookingModal";

import { CalenderSimpleIcon, CarFrontIcon, Location2Icon } from "../../../../../../utils/Icons";
import { useSelector } from "react-redux";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import backend from "../../../../../../api";

export const FUEL_TYPES = [
  { value: "reg", label: "Regular (87 Octane)" },
  { value: "mid", label: "Diesel " },
  { value: "prem", label: "Premium (91-93 Octane)" },
];
export const FUEL_LEVELS = [
  { value: "empty", label: "Empty" },
  { value: "quarter", label: "Quarter Tank" },
  { value: "half", label: "Half Tank" },
  { value: "threeQuarter", label: "Three-Quarter Tank" },
  { value: "full", label: "Full" },
];
export const REQUESTED_AMOUNTS = [
  { value: "5", label: "5 Gallons" },
  { value: "10", label: "10 Gallons" },
  { value: "15", label: "15 Gallons" },
  { value: "20", label: "20 Gallons" },
  { value: "25", label: "25 Gallons" },
  { value: "30", label: "30 Gallons" },
];

const EMPTY_APPT = {
  appointment: {
    clientId: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    notes: "",
    date: "",
    timeSlot: "",
  },
  vehicle: {
    selectedVehicleId: "",
    make: "",
    model: "",
    license: "",
    fuelTypeId: "",
    fuelLevelId: "",
    requestedAmount: "",
  },
  extraVehicles: [],
  location: {
    selectedAddressId: "",
    address1: "",
    city: "",
    country: "",
    region: "",
    postalCode: "",
    neighborhood: "",
    gateCode: "",
    parkingSpotOrNotes: "",
    additionalNotes: "",
    confirmGasCap: false,
  },
};

const STORAGE_KEY = "fuelApptDraft";
const slideVariants = { initial: { x: 10, opacity: 0 }, animate: { x: 0, opacity: 1 }, exit: { x: -10, opacity: 0 } };
const PRICE_KEY = { reg: "regular", mid: "mid", prem: "premium" };
const TYPE_PARAM_TO_FUEL = { regular: "reg", Diesel: "mid", premium: "prem" };

export default function AddAppointment() {
  const scrollerRef = useRef(null);
  const firstStepRender = useRef(true);
  const navigate = useNavigate();
  const { id: bookingId } = useParams();
  const [activeStep, setActiveStep] = useState(1);
  const [draft, setDraft] = useState(EMPTY_APPT);
  const [service, setService] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [rush, setRush] = useState(false);
  const [saving, setSaving] = useState(false);

  const { user, token } = useSelector((s) => s?.auth);
  const company = useSelector((s) => s?.companyInfo?.info);
  const companyId = company?._id || "68120023c24987567a7249fe";

  const [searchParams] = useSearchParams();
  const types = searchParams.getAll("type");
  const rushFromQuery = searchParams.get("rush") === "1";

  // client & team lists
  const [clientOptions, setClientOptions] = useState([]);
  const [memberOptions, setMemberOptions] = useState([]);
  const [teamMemberIds, setTeamMemberIds] = useState([]);
  const [selectedClientUser, setSelectedClientUser] = useState(null);

  // auto-assign flags
  const [autoAssigning, setAutoAssigning] = useState(false);
  const [teamTouched, setTeamTouched] = useState(false);

  const savedVehicles = useMemo(() => {
    const arr = selectedClientUser?.customerProfile?.vehicle?.vehicles ?? [];
    return (arr || []).map((v, i) => ({
      value: String(i),
      label: `${v.make || ""} ${v.model || ""}${v.plate ? " — " + v.plate : ""}`.trim(),
      make: v.make || "",
      model: v.model || "",
      license: v.plate || "",
    }));
  }, [selectedClientUser]);

  const savedAddresses = useMemo(() => {
    const loc = selectedClientUser?.customerProfile?.location;
    if (!loc || !loc.address) return [];
    return [
      {
        value: "addr1",
        label: `${loc.address}${loc.city ? ", " + loc.city : ""}${loc.state ? ", " + loc.state : ""}${loc.postalCode ? " " + loc.postalCode : ""}`,
        address1: loc.address || "",
        city: loc.city || "",
        region: loc.state || "",
        postalCode: loc.postalCode || "",
        neighborhood: loc.neighborhood || "",
        gateCode: loc.gateCode || "",
        parkingSpotOrNotes: loc.parkingSpot || "",
      },
    ];
  }, [selectedClientUser]);

  // load draft from local storage if creating
  useEffect(() => {
    if (bookingId) return;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      if (parsed.activeStep) setActiveStep(parsed.activeStep);
      if (parsed.draft) setDraft(parsed.draft);
    } catch {}
  }, [bookingId]);

  // persist while creating
  useEffect(() => {
    if (bookingId) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ activeStep, draft }));
  }, [activeStep, draft, bookingId]);

  // preselect fuel & rush from query
  useEffect(() => {
    if (!draft.vehicle.fuelTypeId && types?.length) {
      const mapped = TYPE_PARAM_TO_FUEL[types[0]] || "";
      if (mapped) {
        setDraft((p) => ({ ...p, vehicle: { ...p.vehicle, fuelTypeId: mapped } }));
      }
    }
    setRush(rushFromQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [types, rushFromQuery]);

  // fetch service
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const { data } = await backend.get(`/api/services/by-company/${companyId}`);
        if (data?.ok) setService(data.data || null);
      } catch (e) {
        console.error("Fetch service error:", e);
      }
    })();
  }, [companyId]);

  // fetch clients & employees
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const [cust, emp] = await Promise.all([
          backend.get(`/api/user/by-company`, { params: { companyId, role: "customer" } }),
          backend.get(`/api/user/by-company`, { params: { companyId, role: "employee" } }),
        ]);
        const toOpt = (u) => ({
          value: u._id,
          label: u.name || [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email,
          sublabel: u.email,
        });
        setClientOptions((cust.data?.items || []).map(toOpt));
        setMemberOptions((emp.data?.items || []).map(toOpt));
      } catch (err) {
        console.error("fetch users error", err);
      }
    })();
  }, [companyId]);

  // EDIT MODE: load booking and prefill
  useEffect(() => {
    if (!bookingId) return;
    (async () => {
      try {
        const { data } = await backend.get(`/api/bookings/${bookingId}`);
        const b = data?.data;
        if (!b) return;

        setDraft({
          appointment: {
            clientId: b.userId?._id || b.userId,
            firstName: b.contact?.firstName || "",
            lastName: b.contact?.lastName || "",
            email: b.contact?.email || "",
            phone: b.contact?.phone || "",
            company: b.contact?.company || "",
            notes: b.contact?.notes || "",
            date: b.appointment?.date || "",
            timeSlot: b.appointment?.timeSlot || "",
          },
          vehicle: {
            selectedVehicleId: "",
            make: b.vehicles?.[0]?.make || "",
            model: b.vehicles?.[0]?.model || "",
            license: b.vehicles?.[0]?.license || "",
            fuelTypeId: b.vehicles?.[0]?.fuelTypeId || "",
            fuelLevelId: "",
            requestedAmount: b.vehicles?.[0]?.requestedAmount || "",
          },
          extraVehicles: (b.vehicles || [])
            .slice(1)
            .map((v) => ({
              make: v.make,
              model: v.model,
              license: v.license,
              fuelTypeId: v.fuelTypeId,
              fuelLevelId: "",
              requestedAmount: v.requestedAmount,
            })),
          location: {
            selectedAddressId: "",
            address1: b.location?.address1 || "",
            city: b.location?.city || "",
            region: b.location?.region || "",
            postalCode: b.location?.postalCode || "",
            neighborhood: b.location?.neighborhood || "",
            gateCode: b.location?.gateCode || "",
            parkingSpotOrNotes: b.location?.parkingSpotOrNotes || "",
            additionalNotes: b.location?.additionalNotes || "",
            confirmGasCap: false,
          },
        });

        setTeamMemberIds(
          (b.teamMemberIds || [])
            .map((id) => (typeof id === "string" ? id : id?._id))
            .filter(Boolean)
        );
        setRush(Boolean(b.rush));

        if (b.userId?._id || b.userId) {
          const { data: ures } = await backend.get(`/api/user/${b.userId?._id || b.userId}`);
          setSelectedClientUser(ures?.data || null);
        }
      } catch (err) {
        console.error("load booking error", err);
      }
    })();
  }, [bookingId]);

  /* setters */
  const go = (n) => setActiveStep(n);
  const next = () => setActiveStep((p) => Math.min(p + 1, 3));
  const back = () => setActiveStep((p) => Math.max(p - 1, 1));

  const setAppointment = useCallback((partial) => {
    setDraft((prev) => ({ ...prev, appointment: { ...prev.appointment, ...partial } }));
  }, []);
  const setVehicle = useCallback((partial) => {
    setDraft((prev) => ({ ...prev, vehicle: { ...prev.vehicle, ...partial } }));
  }, []);
  const setLocation = useCallback((partial) => {
    setDraft((prev) => ({ ...prev, location: { ...prev.location, ...partial } }));
  }, []);

  const addExtraVehicle = () => {
    setDraft((p) => {
      if (p.extraVehicles.length >= 3) return p;
      return {
        ...p,
        extraVehicles: [
          ...p.extraVehicles,
          { make: "", model: "", license: "", fuelTypeId: "", fuelLevelId: "", requestedAmount: "" },
        ],
      };
    });
  };
  const setExtraVehicle = (idx, partial) => {
    setDraft((p) => {
      const copy = [...p.extraVehicles];
      copy[idx] = { ...copy[idx], ...partial };
      return { ...p, extraVehicles: copy };
    });
  };
  const removeExtraVehicle = (idx) => {
    setDraft((p) => {
      const copy = [...p.extraVehicles];
      copy.splice(idx, 1);
      return { ...p, extraVehicles: copy };
    });
  };

  // client chosen → hydrate contact fields + load saved profile
  const onSelectClient = async (clientId) => {
    setAppointment({ clientId });
    if (!clientId) {
      setSelectedClientUser(null);
      return;
    }
    try {
      const { data } = await backend.get(`/api/user/${clientId}`);
      const u = data?.data;
      setSelectedClientUser(u || null);

      const nameFirst = u?.firstName || (u?.name?.split(" ")[0] || "");
      const nameLast = u?.lastName || (u?.name?.split(" ").slice(1).join(" ") || "");
      setAppointment({
        firstName: nameFirst,
        lastName: nameLast,
        email: u?.email || "",
        phone: u?.phone || "",
        company: u?.customerProfile?.location?.company || "",
      });

      const loc = u?.customerProfile?.location;
      if (loc && !draft.location.address1) {
        setLocation({
          address1: loc.address || "",
          city: loc.city || "",
          region: loc.state || "",
          postalCode: loc.postalCode || "",
          neighborhood: loc.neighborhood || "",
          gateCode: loc.gateCode || "",
          parkingSpotOrNotes: loc.parkingSpot || "",
        });
      }
    } catch (err) {
      console.error("fetch user error", err);
    }
  };

  const parseGallons = (val) => {
    const n = parseFloat(val);
    return Number.isFinite(n) ? n : 0;
  };
  const priceFor = (fuelTypeId) => {
    const key = PRICE_KEY[fuelTypeId] || "regular";
    return Number(service?.prices?.[key] || 0);
  };
  const computeTotals = useCallback(() => {
    let subtotal = parseGallons(draft.vehicle.requestedAmount) * priceFor(draft.vehicle.fuelTypeId);
    for (const ev of draft.extraVehicles) {
      subtotal += parseGallons(ev.requestedAmount) * priceFor(ev.fuelTypeId);
    }
    const deliveryFee = Number(service?.fees?.delivery ?? service?.prices?.deliveryFee ?? 9.99);
    const emergencyFee = rush ? Number(service?.fees?.emergency ?? service?.prices?.emergencyFee ?? 0) : 0;
    const total = Number(subtotal + deliveryFee + emergencyFee);
    return {
      subtotal: Number(subtotal.toFixed(2)),
      deliveryFee: Number(deliveryFee.toFixed(2)),
      emergencyFee: Number(emergencyFee.toFixed(2)),
      total: Number(total.toFixed(2)),
    };
  }, [draft.vehicle, draft.extraVehicles, service, rush]);

  const submitAppointment = async () => {
    const a = draft.appointment;
    const l = draft.location;

    if (!a.clientId) return alert("Please select a client.");
    if (!a.date || !a.timeSlot) return alert("Please select date/time.");
    if (!l.address1 || !l.city || !l.region || !l.postalCode) return alert("Please complete the address.");

    const vehicles = [
      {
        make: draft.vehicle.make,
        model: draft.vehicle.model,
        license: draft.vehicle.license,
        fuelTypeId: draft.vehicle.fuelTypeId,
        requestedAmount: draft.vehicle.requestedAmount || "0",
      },
      ...draft.extraVehicles.map((v) => ({
        make: v.make,
        model: v.model,
        license: v.license,
        fuelTypeId: v.fuelTypeId,
        requestedAmount: v.requestedAmount || "0",
      })),
    ];

    const payload = {
      userId: a.clientId,
      companyId,
      appointment: { date: a.date, timeSlot: a.timeSlot },
      vehicles,
      location: {
        address1: l.address1,
        city: l.city,
        region: l.region,
        postalCode: l.postalCode,
        neighborhood: l.neighborhood,
        gateCode: l.gateCode,
        parkingSpotOrNotes: l.parkingSpotOrNotes,
        additionalNotes: l.additionalNotes,
      },
      contact: {
        firstName: a.firstName,
        lastName: a.lastName,
        email: a.email,
        phone: a.phone,
        company: a.company,
        notes: a.notes,
      },
      teamMemberIds,
      rush,
    };

    try {
      setSaving(true);
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
      let resp;
      if (bookingId) {
        resp = await backend.put(`/api/bookings/${bookingId}`, payload, { headers });
      } else {
        resp = await backend.post(`/api/bookings`, payload, { headers });
      }

      const bk = resp?.data?.booking || resp?.data?.data || null;
      if (bk?._id) {
        if (!bookingId) localStorage.removeItem(STORAGE_KEY);
        navigate(`/view-appointment/details/${bk._id}`);
      } else {
        alert("Saved, but response missing booking.");
      }
    } catch (err) {
      console.error("save booking error:", err);
      alert(err?.response?.data?.error || err.message);
    } finally {
      setSaving(false);
      setShowConfirm(false);
    }
  };

  const openConfirm = () => {
    const a = draft.appointment;
    if (!a.date || !a.timeSlot) return alert("Please select a date/time.");
    if (!draft.location?.address1) return alert("Please complete the address.");
    setShowConfirm(true);
  };

  const steps = [
    { id: 1, label: "Appointment Details", icon: <CalenderSimpleIcon height="16px" fill="white" /> },
    { id: 2, label: "Vehicle & Service Info", icon: <CarFrontIcon height="18px" fill="white" /> },
    { id: 3, label: "Location Info", icon: <Location2Icon height="18px" fill="white" /> },
  ];

  const lockClientSelect = !!bookingId;

  // when admin touches team dropdown, stop auto-assign overwrites
  const handleTeamChange = (ids) => {
    setTeamTouched(true);
    setTeamMemberIds(ids || []);
  };

  // Auto-assign team members based on date/time (create mode only)
  useEffect(() => {
    if (!companyId) return;
    if (bookingId) return;

    const { date, timeSlot } = draft.appointment || {};
    if (!date || !timeSlot) return;
    if (teamTouched) return;

    let cancelled = false;

    (async () => {
      try {
        setAutoAssigning(true);
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
        const { data } = await backend.post(
          "/api/bookings/auto-assign",
          { companyId, date, timeSlot },
          { headers }
        );

        const ids = data?.teamMemberIds || data?.data?.teamMemberIds || [];
        if (!cancelled && Array.isArray(ids) && ids.length) {
          setTeamMemberIds(
            ids.map((x) => (typeof x === "string" ? x : x?._id)).filter(Boolean)
          );
        }
      } catch (err) {
        console.error("auto-assign team error", err);
      } finally {
        if (!cancelled) setAutoAssigning(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId, bookingId, draft.appointment.date, draft.appointment.timeSlot, teamTouched, token]);

  useEffect(() => {
    if (firstStepRender.current) {
      firstStepRender.current = false;
      return;
    }
    const scrollNow = () => {
      const el = scrollerRef.current;

      if (el && (el.scrollHeight > el.clientHeight || getComputedStyle(el).overflowY !== "visible")) {
        el.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        const se = document.scrollingElement || document.documentElement;
        se.scrollTo({ top: 0, behavior: "smooth" });
      }
    };

    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(scrollNow);
      return () => cancelAnimationFrame(raf2);
    });

    return () => cancelAnimationFrame(raf1);
  }, [activeStep]);

  return (
    <div className={Classes.hero}>
      <div className={Classes.body} ref={scrollerRef}>
        <div className={Classes.bodyInner}>
          <motion.div
            className={Classes.stepper}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ type: "tween", duration: 0.35, delay: 0.1 }}
          >
            {steps.map((s, i) => (
              <React.Fragment key={s.id}>
                <div
                  role="button"
                  className={`${Classes.sideElement} ${activeStep === s.id ? Classes.current : ""}`}
                >
                  <div className={Classes.flexAlign10}>
                    <div className={`${Classes.imageCon} ${activeStep >= s.id ? Classes.active : ""}`}>
                      {s.icon}
                    </div>
                    <h5 className={activeStep >= s.id ? Classes.activeText : undefined}>{s.label}</h5>
                  </div>
                </div>
                {i !== steps.length - 1 && (
                  <div className={`${Classes.hr} ${activeStep > s.id ? Classes.coloredHr : ""}`} />
                )}
              </React.Fragment>
            ))}
          </motion.div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeStep}
              variants={slideVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className={Classes.infoMain}
            >
              {activeStep === 1 && (
                <Step1Appointment
                  draft={draft}
                  setAppointment={setAppointment}
                  next={next}
                  service={service}
                  companyId={companyId}              
                  clientOptions={clientOptions}
                  selectedClientId={draft.appointment.clientId}
                  onSelectClient={onSelectClient}
                  memberOptions={memberOptions}
                  selectedTeamIds={teamMemberIds}
                  setSelectedTeamIds={handleTeamChange}
                  lockClientSelect={lockClientSelect}
                  isEdit={!!bookingId}
                />
              )}
              {activeStep === 2 && (
                <Step2Vehicle
                  draft={draft}
                  setVehicle={setVehicle}
                  addExtraVehicle={addExtraVehicle}
                  setExtraVehicle={setExtraVehicle}
                  removeExtraVehicle={removeExtraVehicle}
                  savedVehicles={savedVehicles}
                  onSelectSavedVehicle={(val) => {
                    const v = savedVehicles.find((x) => x.value === val);
                    setDraft((p) => ({
                      ...p,
                      vehicle: {
                        ...p.vehicle,
                        selectedVehicleId: val,
                        make: v?.make || "",
                        model: v?.model || "",
                        license: v?.license || "",
                      },
                    }));
                  }}
                  back={back}
                  next={next}
                />
              )}
              {activeStep === 3 && (
                <Step3Location
                  draft={draft}
                  setLocation={setLocation}
                  savedAddresses={savedAddresses}
                  onSelectSavedAddress={(val) => {
                    const a = savedAddresses.find((x) => x.value === val);
                    setLocation({
                      selectedAddressId: val,
                      address1: a?.address1 || "",
                      city: a?.city || "",
                      region: a?.region || "",
                      postalCode: a?.postalCode || "",
                      neighborhood: a?.neighborhood || "",
                      gateCode: a?.gateCode || "",
                      parkingSpotOrNotes: a?.parkingSpotOrNotes || "",
                    });
                  }}
                  back={back}
                  finish={openConfirm}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <ConfirmBookingModal
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        draft={draft}
        service={service}
        rush={rush}
        setRush={setRush}
        computeTotals={computeTotals}
        onConfirm={() => !saving && submitAppointment()}
        saving={saving}
        isEdit={!!bookingId}
      />
    </div>
  );
}
